import { Injectable } from '@angular/core';
import { Apollo, QueryRef } from 'apollo-angular';
import { ConsentRequest } from '../../../interfaces/consent-request';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import ConsentRequestApproval from '../../../interfaces/consent-request-approval';
import Permission, {
  createDummyPermissions,
} from '../../../interfaces/permission';
import ConsentRequiredStatus from '../../../interfaces/consent-required-status';
import ConsentApprovalStatus from '../../../interfaces/consent-approval-status';
import ID from '../../../types/id';
import {
  GET_REQUEST_WITH_APPROVALS,
  REFRESH_APPROVALS,
  RefreshApprovalsResponse,
  RefreshApprovalsVariables,
  UPDATE_APPROVAL,
  UpdateConsentApprovalTuple,
  UpdateConsentRequestApprovalInput,
  UpdateConsentRequestApprovalVariables,
} from './graphql/individual-approvals';
import { DefaultSnackBarService } from '../../../services/default-snack-bar.service';
import { ApprovalsService } from '../consent-approvals.component';

@Injectable({
  providedIn: 'root',
})
export class IndividualApprovalsService implements ApprovalsService {
  private _consentRequest: ConsentRequest | null = null;
  private consentRequest = new BehaviorSubject(this._consentRequest);
  public consentRequest$ = this.consentRequest.asObservable();

  private _consentRequestApprovals: ConsentRequestApproval[] = [];
  private consentRequestApprovals = new BehaviorSubject(
    this._consentRequestApprovals
  );
  public consentRequestApprovals$ = this.consentRequestApprovals.asObservable();

  private _permission: Permission = createDummyPermissions();
  private permission = new BehaviorSubject(this._permission);
  public permission$ = this.permission.asObservable();

  private _consentRequiredStatuses: ConsentRequiredStatus[] = [];
  private consentRequiredStatuses = new BehaviorSubject(
    this._consentRequiredStatuses
  );
  public consentRequiredStatuses$ = this.consentRequiredStatuses.asObservable();

  private _consentApprovalStatuses: ConsentApprovalStatus[] = [];
  private consentApprovalStatuses = new BehaviorSubject(
    this._consentApprovalStatuses
  );
  public consentApprovalStatuses$ = this.consentApprovalStatuses.asObservable();

  private _loading = false;
  private loading = new BehaviorSubject(this._loading);
  public loading$ = this.loading.asObservable();

  private _approvalsLoading = false;
  private approvalsLoading = new BehaviorSubject(this._approvalsLoading);
  public approvalsLoading$ = this.approvalsLoading.asObservable();

  private requestId: ID;

  private _query: QueryRef<RefreshApprovalsResponse, RefreshApprovalsVariables>;
  private _sub: Subscription;

  constructor(
    public apollo: Apollo,
    private _snackBar: DefaultSnackBarService
  ) {}

  private _setConsentRequest(request: ConsentRequest) {
    this._consentRequest = request;
    this.consentRequest.next(this._consentRequest);
    if (request.consentRequestApprovals)
      this._setConsentRequestApprovals(request.consentRequestApprovals);
  }
  private _setConsentRequestApprovals(approvals: ConsentRequestApproval[]) {
    this._consentRequestApprovals = approvals;
    this.consentRequestApprovals.next(this._consentRequestApprovals);
  }
  private _setPermission(permission: Permission) {
    this._permission = permission;
    this.permission.next(this._permission);
  }
  private _setConsentRequiredStatuses(statuses: ConsentRequiredStatus[]) {
    this._consentRequiredStatuses = statuses;
    this.consentRequiredStatuses.next(this._consentRequiredStatuses);
  }
  private _setConsentApprovalStatuses(statuses: ConsentApprovalStatus[]) {
    this._consentApprovalStatuses = statuses;
    this.consentApprovalStatuses.next(this._consentApprovalStatuses);
  }
  private _setApprovalsLoading(loading: boolean) {
    this._approvalsLoading = loading;
    this.approvalsLoading.next(this._approvalsLoading);
  }
  private _setLoading(loading: boolean) {
    this._loading = loading;
    this.loading.next(this._loading);
    this._setApprovalsLoading(loading);
  }

  public getRequestWithApprovals(id: ID) {
    this._setLoading(true);
    this.apollo
      .query({
        query: GET_REQUEST_WITH_APPROVALS,
        variables: { id },
        fetchPolicy: 'network-only',
      })
      .subscribe(({ data }) => {
        this._setLoading(false);
        if (!data) return;
        this._setConsentApprovalStatuses(data.getConsentApprovalStatuses);
        if (data.me?.permissionsForResource) {
          this._setPermission(data.me.permissionsForResource);
        }
        this._setConsentRequiredStatuses(data.getConsentRequiredStatuses);

        this._setConsentRequest(data.getConsentRequest);
      });
  }
  public refreshApprovals(consent_request_id: ID) {
    this._setApprovalsLoading(true);
    if (!this._query || !this._sub || this.requestId !== consent_request_id) {
      this._query = this.apollo.watchQuery({
        query: REFRESH_APPROVALS,
        variables: { consent_request_id },
        fetchPolicy: 'network-only',
      });
      this._sub = this._query.valueChanges.subscribe(({ data }) => {
        if (data) {
          this._setConsentRequestApprovals(
            data.getConsentRequestApprovals as ConsentRequestApproval[]
          );
        }
        this._setApprovalsLoading(false);
      });
    } else {
      this._query.refetch();
    }
  }
  public getRequiredStatus(
    consent_required_status_id: ID | null
  ): ConsentRequiredStatus | null {
    return (
      this._consentRequiredStatuses.find(
        (status) => status.id === consent_required_status_id
      ) ?? null
    );
  }
  public getApprovalStatus(
    consent_approval_status_id: ID | null
  ): ConsentApprovalStatus | null {
    return (
      this._consentApprovalStatuses.find(
        (status) => status.id === consent_approval_status_id
      ) ?? null
    );
  }
  public updateApprovals(
    consent_request_id: ID,
    inputs: UpdateConsentApprovalTuple[]
  ) {
    let observables: ReturnType<Apollo['mutate']>[] = [];
    for (let tuple of inputs) {
      observables.push(
        this.apollo.mutate({
          mutation: UPDATE_APPROVAL,
          variables: {
            id: tuple[0],
            input: tuple[1],
          },
        })
      );
    }
    let sub = combineLatest(observables).subscribe((all) => {
      this._snackBar.open('Saved approval data');
      setTimeout(()=>this.refreshApprovals(consent_request_id), 100);
      sub?.unsubscribe();
    });
  }
  public tearDown() {
    this._sub?.unsubscribe();
  }
}
